import React, {
  createContext, useCallback, useContext, useMemo, useState,
} from 'react';

import type { StreamId, StreamRestreamResponse } from '@vlabs/api/artemy/model';
import { useStreamRestream } from '@vlabs/monokit/shared/lib/video';

interface RestreamContext {
  streamId: StreamId;
  quality: number | undefined;
  setQuality: (quality: number | undefined) => void;
  originalQuality: number | undefined;
  status: 'loading' | 'success' | 'error';
  retransmission: StreamRestreamResponse | undefined;
  attempt: number;
  reconnect: VoidFunction;
}

const RestreamContext = createContext<Partial<RestreamContext>>({});

interface RestreamProviderProps {
  streamId: StreamId;
  children: React.ReactNode;
}

// Специализированный контекст ретрансляции: качество, состояние подключения,
// переподключение — общие для шапки, плеера и fullscreen-панели.
function RestreamProvider({
  streamId,
  children,
}: RestreamProviderProps): JSX.Element {
  const [quality, setQuality] = useState<number | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);

  const { retransmission, status, reconnect } = useStreamRestream(streamId, quality);

  const handleReconnect = useCallback(() => {
    reconnect();
    setAttempt((value) => value + 1);
  }, [reconnect]);

  const contextValue = useMemo<RestreamContext>(() => ({
    streamId,
    quality,
    setQuality,
    originalQuality: retransmission?.quality,
    // useStreamRestream — js-хук без типизации статуса
    status: status as RestreamContext['status'],
    retransmission,
    attempt,
    reconnect: handleReconnect,
  }), [attempt, handleReconnect, quality, retransmission, status, streamId]);

  return (
    <RestreamContext.Provider value={contextValue}>
      {children}
    </RestreamContext.Provider>
  );
}

const useRestream = (): RestreamContext => useContext<RestreamContext>(
  RestreamContext as React.Context<RestreamContext>,
);

export { RestreamProvider, useRestream };
